/**
 * Applying an approved proposal — **with the mutations that already exist**.
 *
 * There isn't a single new SQL statement here. Each item translates to the
 * same mutation the manual editor uses (`addUnitMutation`,
 * `addLessonModuleMutation`, `addBlockMutation`, `updateBlockMutation`, …),
 * which writes through PowerSync's normal write-path. That gets three things
 * for free that, written separately, would be three more features: the
 * **activity feed**, the **trash bin**, and **undo**.
 *
 * Two invariants:
 *
 * 1. **Only what's approved and valid gets applied.** The verdict is set by
 *    `validate.ts` crossing the two registries; this function doesn't
 *    validate anything on its own nor trust the `valid` the server brought.
 * 2. **The order is by dependency**, not the one the model returned: the
 *    unit before its module, the module before its block. Otherwise,
 *    children are left pointing at parents that don't exist yet.
 */

import type { PowerSyncDrizzleDB } from "@space/powersync";
import { addBlockMutation, addUnitMutation, updateBlockMutation } from "../../courses/mutations";
import {
  addCourseModuleMutation,
  addLessonModuleMutation,
  addResourceModuleMutation,
  updateCourseModuleMutation,
} from "../modules/mutations";
import type { AiItemDecision, AiProposalItem } from "./contract";
import { type ItemVerdict, sortForApply } from "./validate";

export interface ApplyContext {
  db: PowerSyncDrizzleDB;
  courseId: string;
  /** Target unit when the proposal doesn't create its own (standalone blocks). */
  defaultUnitId?: string;
  /** Target lesson for `block` items of a content proposal. */
  defaultLessonId?: string;
}

export interface ApplyResult {
  /** Decisions to send to the server (`POST /proposals/:id/decide`). */
  decisions: AiItemDecision[];
  /** Ids created, by item `path`. */
  createdByPath: Map<string, string>;
  failures: { itemId: string; message: string }[];
}

function text(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function num(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/** Unit an item belongs to, resolving the already-created parent. */
function parentUnitId(item: AiProposalItem, created: Map<string, string>, ctx: ApplyContext) {
  // `/units/2/modules/0` → its unit is `/units/2`.
  const match = /^(\/units\/\d+)\//.exec(item.path);
  const parentPath = match?.[1];
  if (parentPath) {
    const parent = created.get(parentPath);
    if (parent) {
      return parent;
    }
  }
  const declared = text(item.payload.parentId) || text(item.payload.unitId);
  return declared || ctx.defaultUnitId || "";
}

/**
 * Applies the approved items and returns the decisions that need recording.
 *
 * It's tolerant to per-item failures on purpose: one lesson failing can't
 * take down the other four that were valid, and what fails stays marked
 * `failed` with its reason instead of disappearing.
 */
export async function applyApprovedItems(
  items: readonly AiProposalItem[],
  verdicts: ReadonlyMap<string, ItemVerdict>,
  approvedIds: ReadonlySet<string>,
  ctx: ApplyContext,
): Promise<ApplyResult> {
  const decisions: AiItemDecision[] = [];
  const createdByPath = new Map<string, string>();
  const failures: { itemId: string; message: string }[] = [];

  for (const item of sortForApply(items)) {
    if (!approvedIds.has(item.id)) {
      continue;
    }
    const verdict = verdicts.get(item.id);
    if (!verdict?.valid) {
      // Defense in depth: the server also rejects it (`AND valid`), but an
      // application we know is invalid isn't sent to it.
      decisions.push({
        itemId: item.id,
        status: "rejected",
        valid: false,
        errors: verdict?.errors ?? ["la pieza no pasó la validación"],
      });
      continue;
    }
    try {
      const entityId = await applyOne(item, createdByPath, ctx);
      if (entityId) {
        createdByPath.set(item.path, entityId);
      }
      decisions.push({ itemId: item.id, status: "applied", entityId, valid: true, errors: [] });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      failures.push({ itemId: item.id, message });
      decisions.push({ itemId: item.id, status: "failed", valid: true, errors: [message] });
    }
  }
  return { decisions, createdByPath, failures };
}

async function applyOne(
  item: AiProposalItem,
  created: Map<string, string>,
  ctx: ApplyContext,
): Promise<string | undefined> {
  const { db, courseId } = ctx;
  switch (item.entity) {
    case "unit": {
      if (item.op === "create") {
        return addUnitMutation(db, courseId, {
          title: text(item.payload.title, "Nueva unidad"),
          position: num(item.payload.position, 0),
        });
      }
      break;
    }
    case "course_module": {
      const unitId = parentUnitId(item, created, ctx);
      if (!unitId) {
        throw new Error("cannot determine which unit this module belongs to");
      }
      if (item.op === "create") {
        const kind = text(item.payload.kind);
        const title = text(item.payload.title, "New module");
        const position = num(item.payload.position, 0);
        // Kinds with their own detail use their COMPOSITE mutation, which
        // creates the detail row and its spine module in one go. Creating
        // just the module would leave a dangling `ref_id`.
        if (kind === "lesson") {
          const { moduleId } = await addLessonModuleMutation(db, {
            courseId,
            unitId,
            title,
            position,
          });
          return moduleId;
        }
        if (kind === "resource") {
          const { moduleId } = await addResourceModuleMutation(db, {
            courseId,
            unitId,
            title,
            position,
          });
          return moduleId;
        }
        return addCourseModuleMutation(db, {
          courseId,
          unitId,
          kind,
          position,
          config: item.payload.config ?? {},
        });
      }
      if (item.op === "update" && item.entityId) {
        await updateCourseModuleMutation(db, item.entityId, {
          config: item.payload.config ?? {},
          position: num(item.payload.position, 0),
        });
        return item.entityId;
      }
      break;
    }
    case "block": {
      const lessonId = text(item.payload.lessonId) || ctx.defaultLessonId || "";
      if (item.op === "create") {
        if (!lessonId) {
          throw new Error("cannot determine which lesson this block belongs to");
        }
        return addBlockMutation(db, {
          courseId,
          lessonId,
          kind: text(item.payload.type),
          content: item.payload.def ?? {},
          position: num(item.payload.position, 0),
          weight: num(item.payload.weight, 1),
        });
      }
      if (item.op === "update" && item.entityId) {
        await updateBlockMutation(db, item.entityId, { content: item.payload.def ?? {} });
        return item.entityId;
      }
      break;
    }
    default: {
      break;
    }
  }
  throw new Error(`cannot apply "${item.op} ${item.entity}" automatically`);
}
